import { CampaignDetailItem } from "@/lib/types";
import { showSuccess, showApiError } from "@/lib/toast";
import {
  deleteCampaignDetailDate,
  getCampaignDetailItems,
  getCampaignDetailDate,
  updateCampaignDetailItem,
  getCampaignDetailContent,
  deleteCampaignDetailContent,
} from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { formatDateToLocal, formatToUTC } from "@/lib/dateUtils";
import { addDays } from "date-fns";
import { CampaignRobotItemMetaForm, FormSchemaType } from "./CampaignRobotItemMetaForm";

type Props = {
  item: CampaignDetailItem;
};

export const CampaignRobotItemUpdateForm: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const campaignDetailItems = useQuery({
    queryKey: ["getCampaignDetailItems", item.campaignintId],
    queryFn: () => getCampaignDetailItems(String(item.campaignId), String(activeFirm?.firmGuid)),
  });

  const campaignDetailItemsData = campaignDetailItems.data?.data.result || [];

  const { data: campaignDetailDate, isLoading: isCampaignDetailDateLoading } = useQuery({
    queryFn: () =>
      getCampaignDetailDate(
        { numberOfObjectsPerPage: 999999, pageNumber: 1 },
        { Firm_Id: activeFirm?.firmGuid, CampaignDetail_Id: item.id }
      ),
    queryKey: ["getCampaignDetailDate", activeFirm?.firmGuid, item.id],
  });

  const campaignDetailDateData = campaignDetailDate?.data.result || [];

  const { data: campaignDetailContent, isLoading: isCampaignDetailContentLoading } = useQuery({
    queryFn: () => getCampaignDetailContent(String(item.id), String(activeFirm?.firmGuid)),
    queryKey: ["getCampaignDetailContent", activeFirm?.firmGuid, item.id],
  });
  const campaignDetailContentData = campaignDetailContent?.data.result || [];

  const queryClient = useQueryClient();
  const updateEventMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      const campaignDetailItem = campaignDetailItemsData.find((r) => r.id === item.id);
      if (!campaignDetailItem) return Promise.reject();

      for (const existing of campaignDetailContentData) {
        await deleteCampaignDetailContent(existing.id, String(activeFirm?.firmGuid));
      }

      for (const existing of campaignDetailDateData) {
        await deleteCampaignDetailDate(existing.id, String(activeFirm?.firmGuid));
      }

      if (values.periodEnd === values.periodStart) {
        values.periodEnd = addDays(formatToUTC(values.periodEnd), 1).toISOString();
      } else {
        values.periodEnd = formatToUTC(values.periodEnd);
      }

      return updateCampaignDetailItem({
        campaignDetailItemId: campaignDetailItem.id,
        campaignId: Number(campaignDetailItem.campaignintId),
        periodStart: formatToUTC(values.periodStart),
        periodEnd: values.periodEnd,
        isActive: values.isActive,
        firmId: String(activeFirm?.firmGuid),
        contentGroupId: values.contentGroupId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailItems"] });
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailDate"] });
      queryClient.invalidateQueries({ queryKey: ["getCampaignDetailContent"] });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (isCampaignDetailContentLoading) return <LoadingSmall />;

  return (
    <>
      {isCampaignDetailDateLoading ? (
        <LoadingSmall />
      ) : (
        <CampaignRobotItemMetaForm
          buttonText={t("common.save")}
          onSubmit={(values) => updateEventMutation.mutate(values)}
          isLoading={updateEventMutation.isPending}
          defaultValues={{
            periodStart: item.periodStart ? formatDateToLocal(item.periodStart, false) : "",
            periodEnd: item.periodEnd ? formatDateToLocal(item.periodEnd, false) : "",
            isActive: item.isActive,
            contentGroupId: item.contentGroupintId,
          }}
        />
      )}
    </>
  );
};
